import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux';
import { matchPath, useNavigate, useParams } from 'react-router-dom';

import { useMediaQuery } from '@material-ui/core';
import { DEPOSIT_CONSTANTS } from 'deposit/config/constants';
import DEPOSIT_ROUTE_PATHS from 'deposit/config/routes/route-paths';
import PAYMENT_LINK_ROUTE_PATHS from 'payment-link/config/routes/route-paths';

import AmplitudeAnalytics from '../services/amplitude/analytics';
import { AmplitudeSourceType } from '../services/amplitude/types';
import { ErrorMessage, mkError } from '../services/tracker-service';
import { PreviewAuthResponse, PurchaseStatus } from 'deposit/services/deposit-service';
import { DepositStatus, MerchantDepositDetail, postAction } from 'deposit/services/types';
import { BackFromProfile } from '../store/global/types';

import { Country } from 'common/types/global';
import { Notification, NotificationSeverity } from 'common/types/notifications';
import { CommonFunnelEventNames, Error, ErrorsFraud, ErrorTypes } from 'common/types/services';

import { ToastContext } from '../components/toast-notification/toast-notification-container';
import { translate } from '../config/i18n';
import { TrackService } from '../services';
import { getDepositInfo, setNeedsValidation } from '../store/deposit';
import { handleError, setBackFromProfile, setRequiredFields } from '../store/global';
import { setPhoneNumber } from '../store/session';
import type { AppDispatch, RootState } from '../store/store';
import { buildPathWithParams, getQueryParam } from './url';
import { toCamelCaseObject } from './utils';

export const useAppDispatch = () => useDispatch<AppDispatch>();
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;

export const useGetToken = () => {
  const { token } = useParams<{ token: string }>();
  if (token) {
    return token;
  }
  const urlsWithToken = Object.values(DEPOSIT_ROUTE_PATHS).filter(
    (url) => url.indexOf(':token') > -1,
  );
  let tokenResult = '';

  for (const url of urlsWithToken) {
    const match = matchPath({ path: url }, window.location.pathname);
    if (match && match.params.token) {
      tokenResult = match.params.token;
      break;
    }
  }

  return tokenResult;
};

export const useDesktopMode = () => useMediaQuery('(min-width: 700px)');

export const useGetIntegrationData = () => {
  const sizing = `${window.self.innerWidth}x${window.self.innerHeight}`;
  const merchantIframe = window.top !== window.self;
  const redirectionType = history.length > 1 ? 'REDIRECT' : 'NEW_WINDOW_TAB';

  return {
    'Viewport Size': sizing,
    'Merchant Iframe': merchantIframe,
    'Merchant Redirection': redirectionType,
  };
};

export const useToast = () => useContext(ToastContext);

export const useCallOnInit = <T>(
  whatToCall: () => T,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  deps: ReadonlyArray<any> = [],
): T | undefined => {
  const [res, setRes] = useState<T | undefined>(undefined);

  useEffect(() => {
    const response = whatToCall();
    if (response) {
      setRes(response);
    }
  }, deps);

  return res;
};

export const useHandleRequiredFields = () => (
  error: Error,
  from: BackFromProfile,
  ROUTE_PATHS: typeof DEPOSIT_ROUTE_PATHS | typeof PAYMENT_LINK_ROUTE_PATHS,
  dispatch: ReturnType<typeof useAppDispatch>,
  navigate: ReturnType<typeof useNavigate>,
  token: string,
  enqueueToast: (toast: Notification) => void,
  defaultMsgError = 'deposit_invalid_request_desc',
  merchant?: { id?: number; name?: string },
  callback?: (e: Error) => void,
  enforcedPaymentMethod?: boolean,
) => {
  callback?.(error);
  const hasRequiredFields = !!error.required_fields;
  const requiredFields: string[] = error.required_fields ?? [];
  const isUserActionRequired =
    requiredFields.some(
      (reqField) => reqField === 'kyc_documents' || reqField === 'sof_documents',
    ) && requiredFields.length === 1;

  if (hasRequiredFields) {
    //AMPLITUDE IDENTITY TRACKER
    AmplitudeAnalytics.requiredFieldsRequested(
      token,
      AmplitudeSourceType.MERCHANT_SITE,
      merchant,
      requiredFields.includes('kyc_documents'),
      requiredFields.includes('sof_documents'),
      requiredFields,
    );
  }

  if (hasRequiredFields && !isUserActionRequired) {
    TrackService.trackEvent(CommonFunnelEventNames.pmRequiredFieldNoKycSelected);
    dispatch(setRequiredFields(requiredFields));
    dispatch(setBackFromProfile(from));
    setTimeout(() => {
      navigate(buildPathWithParams(ROUTE_PATHS.profile, { token }), { replace: true });
    }, 100);
  } else {
    //automatic redirection
    if (isUserActionRequired && !error.error.includes('merchant_gateway')) {
      TrackService.trackEvent(CommonFunnelEventNames.pmRequiredFieldWithKycSelected);
      navigate(buildPathWithParams(ROUTE_PATHS.identityValidation, { token }), { replace: true });
      dispatch(setBackFromProfile(from));
      return;
    }
    if (from === BackFromProfile.CARD && !useIsFraudError(error.error) && !isUserActionRequired) {
      enqueueToast({
        text: translate(error.description ?? defaultMsgError),
        severity: NotificationSeverity.ERROR,
      });
      return;
    }
    handleNeedsValidation(
      toCamelCaseObject<PreviewAuthResponse>(error as PreviewAuthResponse),
      dispatch,
      enqueueToast,
      defaultMsgError,
      navigate,
      token,
      ROUTE_PATHS,
      from,
      enforcedPaymentMethod,
      isUserActionRequired,
    );
  }
};

export const useHandleRedirectErrors = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  /**
   * Handle redirect errors (and do the redirection)
   * @param {Error}  error - The error object returned from the API
   * @returns {boolean} -Indicate if it is a redirect error - If true, you need to stop your method execution
   */
  const handleRedirectError = (
    error: Error,
    ROUTE_PATHS: typeof DEPOSIT_ROUTE_PATHS | typeof PAYMENT_LINK_ROUTE_PATHS,
    enforcedPaymentMethod?: boolean,
  ): boolean => {
    const errorDescription = DEPOSIT_CONSTANTS.RedirectErrors.includes(error.error)
      ? 'one_touch_operation_security_error'
      : error.description;
    if (DEPOSIT_CONSTANTS.RedirectErrors.includes(error.error) || enforcedPaymentMethod) {
      dispatch(
        handleError({
          error: error.error,
          description: errorDescription,
          status: 412,
        }),
      );
      navigate(ROUTE_PATHS.error, { replace: true });
      TrackService.trackError(mkError(error, ErrorMessage.REDIRECT_ERROR));
      return true;
    }
    return false;
  };

  return handleRedirectError;
};

const summaryRedirection = (
  from: BackFromProfile,
  navigate: ReturnType<typeof useNavigate>,
  ROUTE_PATHS: typeof DEPOSIT_ROUTE_PATHS | typeof PAYMENT_LINK_ROUTE_PATHS,
  token: string,
) => {
  if (from === BackFromProfile.SUBSCRIPTION && 'subscriptionSummary' in ROUTE_PATHS) {
    return navigate(buildPathWithParams(ROUTE_PATHS.subscriptionSummary, { token }), {
      replace: true,
    });
  }
  if (from === BackFromProfile.CARD && 'manual' in ROUTE_PATHS) {
    return navigate(buildPathWithParams(ROUTE_PATHS.manual, { token }), { replace: true });
  }
  if (from === BackFromProfile.WALLET && 'walletSummary' in ROUTE_PATHS) {
    return navigate(buildPathWithParams(ROUTE_PATHS.walletSummary, { token }), { replace: true });
  }
  if (from === BackFromProfile.REVERSE && 'checkoutPage' in ROUTE_PATHS) {
    return navigate(buildPathWithParams(ROUTE_PATHS.checkoutPage, { token }), { replace: true });
  }
  if (from === BackFromProfile.REVERSE_APC || from === BackFromProfile.ANONYMOUS_CARD) {
    return;
  }
  if (from === BackFromProfile.AUTH && 'selectMethod' in ROUTE_PATHS) {
    return navigate(buildPathWithParams(ROUTE_PATHS.selectMethod, { token }), { replace: true });
  }
  return navigate(buildPathWithParams(ROUTE_PATHS.paymentSummary, { token }), { replace: true });
};

export const useIsFraudError = (error: string) =>
  Object.values(ErrorsFraud).includes(error as ErrorsFraud);

export const handleNeedsValidation = (
  error: PreviewAuthResponse,
  dispatch: ReturnType<typeof useAppDispatch>,
  enqueueToast: (toast: Notification) => void,
  defaultMsgError: string,
  navigate: ReturnType<typeof useNavigate>,
  token: string,
  ROUTE_PATHS: typeof DEPOSIT_ROUTE_PATHS | typeof PAYMENT_LINK_ROUTE_PATHS,
  from: BackFromProfile,
  enforcedPaymentMethod?: boolean,
  isUserActionRequired?: boolean,
) => {
  if (
    Object.values(ErrorTypes).includes(error.internalTerm as ErrorTypes) ||
    useIsFraudError(error.error) ||
    isUserActionRequired
  ) {
    dispatch(setNeedsValidation(error));
    summaryRedirection(from, navigate, ROUTE_PATHS, token);
  } else {
    if (enforcedPaymentMethod) {
      dispatch(
        handleError({
          error: error.error,
          description: error.description,
          status: 412,
        }),
      );
      navigate(ROUTE_PATHS.error);
      TrackService.trackError(mkError(error, ErrorMessage.REDIRECT_ERROR));
      return null;
    }
    enqueueToast({
      text: translate(error.description ?? defaultMsgError),
      severity: NotificationSeverity.ERROR,
    });
    if (from === BackFromProfile.SUBSCRIPTION && 'subscriptionSelectMethodPage' in ROUTE_PATHS) {
      navigate(buildPathWithParams(ROUTE_PATHS.subscriptionSelectMethodPage, { token }), {
        replace: true,
      });
      return;
    }

    if (
      from === BackFromProfile.CARD ||
      from === BackFromProfile.REVERSE_APC ||
      from === BackFromProfile.ANONYMOUS_CARD
    ) {
      summaryRedirection(from, navigate, ROUTE_PATHS, token);
      return;
    }

    navigate(buildPathWithParams(ROUTE_PATHS.selectMethod, { token }), { replace: true });

    throw error.description;
  }
};

export const useWindowOpener = (
  windowLocation: string,
  tab: Window,
  status: PurchaseStatus,
  token?: string,
) => {
  if (!windowLocation || status !== PurchaseStatus.PENDING) {
    tab.close();
    return;
  }
  //notify to parent
  parent.postMessage(
    {
      action: 'close',
      depositStatusNotifify: {
        status: DepositStatus.PURCHASE_PENDING,
        depositExternalId: token && token,
      },
    },
    '*',
  );

  tab.location.href = windowLocation;
};

export const getDefaultCountry = (
  countries: Country[],
  depositInfo: MerchantDepositDetail,
  dispatch: ReturnType<typeof useAppDispatch>,
) => {
  if (depositInfo.user && !!depositInfo.user.phone) {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [code, number] = depositInfo.user.phone.split(' ');
    const country = countries.find((country) => country.callingCode === code);

    dispatch(setPhoneNumber(depositInfo.user.phone));

    if (country) {
      return country.iso;
    }
  }
  if (depositInfo.deposit.paymentMethodEnforced) {
    return depositInfo.deposit.country;
  }
  return depositInfo.countryByIp ?? depositInfo.deposit.country;
};

export const useParentPostMessage = (
  destination: Window | null,
  //eslint-disable-next-line
  message: Record<string, any>,
  notifySelf = false,
  target?: string,
  postActionRedirect?: boolean,
) => {
  const sdkMessage = {
    action: message.action,
    depositStatusNotify: !!message.message ?? message.depositStatusNotify,
  };
  if (notifySelf && !postActionRedirect) {
    const opener = window.opener;
    opener && opener.postMessage(sdkMessage, target ?? '*');

    if (message.action === 'close') {
      window.close();
    }
  }
  destination && destination.postMessage(sdkMessage, '*');
};

export const useCheckParent = () => {
  const parentWindow = window.opener;
  const isSdk = getQueryParam('condensed');
  const depositInfo = useAppSelector(getDepositInfo);
  const postActionRedirect = depositInfo?.postAction === postAction.REDIRECT;

  const handleBeforeUnload = useCallback(() => {
    parentWindow &&
      parentWindow.postMessage({ action: 'close', message: { status: 'pending' } }, '*');
  }, [parentWindow]);

  if (!!parent && isSdk && !postActionRedirect) {
    window.addEventListener('beforeunload', handleBeforeUnload);
  }
};

export const useIsMounted = () => {
  const ref = useRef(false);
  useEffect(() => {
    ref.current = true;
    return () => {
      ref.current = false;
    };
  }, []);

  return useCallback(() => {
    return ref.current;
  }, [ref]);
};

export const useShowTaxesInformation = (country?: string) => {
  if (country === 'BR') {
    return translate('one_touch_taxes_included');
  }
  return;
};
